import PropTypes from "prop-types";
import { useState } from "react";
import moment from "moment-timezone";
import { Controller, useFormContext } from "react-hook-form";
import { CalendarIcon, XIcon } from "lucide-react";
import get from "lodash/get";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { dateNavBounds } from "components/NewAdvancedSearch/filters/dateNavBounds";

import { REQUIRED_MARK } from "./fieldClasses";

const TIME_FORMAT = "HH:mm";
const DISPLAY_FORMAT = "ll, hh:mm A";

const toZoned = (value, timeZone) => {
  if (!value) return null;
  const m = moment.utc(value);
  return m.isValid() ? m.tz(timeZone) : null;
};

// The calendar works on local-time Dates, so the zoned wall-clock day is
// rebuilt as a local Date; converting the instant directly could land on the
// neighbouring day when the browser and the display zone differ.
const toCalendarDay = zoned =>
  zoned ? new Date(zoned.year(), zoned.month(), zoned.date()) : undefined;

const toUtcIso = (day, time, timeZone) => {
  const [hour, minute] = time.split(":").map(Number);
  return moment
    .tz(
      {
        year: day.getFullYear(),
        month: day.getMonth(),
        date: day.getDate(),
        hour,
        minute,
      },
      timeZone
    )
    .toISOString();
};

/**
 * Date-and-time picker: a button that opens a calendar and a time input in a
 * popover. The user picks the wall-clock time in `timeZone`; the value is the
 * matching UTC instant as an ISO 8601 string, or null when empty. The time
 * input stays disabled until a day is picked, and a new day keeps the time
 * already chosen (midnight when there is none). The popover renders in a
 * portal, so cards and scrolling tables do not clip it.
 */
const FormDateTimePicker = ({
  name,
  label,
  required,
  timeZone,
  placeholder = "Pick a date and time",
  disabled,
  id,
  "aria-label": ariaLabel,
  onValueChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const triggerId = id ?? name;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const zoned = toZoned(field.value, timeZone);
        const selected = toCalendarDay(zoned);
        const time = zoned ? zoned.format(TIME_FORMAT) : "00:00";
        const isClearable = !required && !disabled && Boolean(field.value);

        const commit = value => {
          field.onChange(value);
          onValueChange?.(value);
        };

        const handleOpenChange = open => {
          setIsOpen(open);
          if (!open) field.onBlur();
        };

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={triggerId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <div className="tw:relative">
              <Popover open={isOpen} onOpenChange={handleOpenChange}>
                <PopoverTrigger
                  disabled={disabled}
                  render={
                    <Button
                      id={triggerId}
                      name={field.name}
                      variant="outline"
                      aria-label={ariaLabel}
                      aria-required={required || undefined}
                      aria-invalid={error ? true : undefined}
                      className={cn(
                        "tw:w-full tw:justify-start tw:font-normal",
                        isClearable && "tw:pr-8"
                      )}
                    />
                  }
                >
                  <CalendarIcon className="tw:text-muted-foreground" />
                  {zoned ? (
                    <span className="tw:truncate">
                      {zoned.format(DISPLAY_FORMAT)}
                    </span>
                  ) : (
                    <span className="tw:truncate tw:text-muted-foreground">
                      {field.value || placeholder}
                    </span>
                  )}
                </PopoverTrigger>
                <PopoverContent
                  className="tw:w-auto tw:overflow-hidden tw:p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={selected}
                    defaultMonth={selected}
                    onSelect={day => {
                      if (day) commit(toUtcIso(day, time, timeZone));
                    }}
                    captionLayout="dropdown"
                    {...dateNavBounds()}
                  />
                  <div className="tw:flex tw:items-center tw:gap-2 tw:border-t tw:p-3">
                    <Input
                      type="time"
                      aria-label={`${label ?? ariaLabel ?? "Date"} time`}
                      value={time}
                      disabled={!selected}
                      onChange={event => {
                        if (selected && event.target.value) {
                          commit(
                            toUtcIso(selected, event.target.value, timeZone)
                          );
                        }
                      }}
                    />
                    <span className="tw:text-xs tw:whitespace-nowrap tw:text-muted-foreground">
                      {timeZone}
                    </span>
                  </div>
                </PopoverContent>
              </Popover>
              {isClearable && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="tw:absolute tw:top-1/2 tw:right-1 tw:-translate-y-1/2"
                  aria-label={`Clear ${label ?? ariaLabel ?? "date"}`}
                  onClick={() => {
                    commit(null);
                    field.onBlur();
                  }}
                >
                  <XIcon />
                </Button>
              )}
            </div>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormDateTimePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  timeZone: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  "aria-label": PropTypes.string,
  onValueChange: PropTypes.func,
};

export default FormDateTimePicker;
